import {
  ActiveFeatureFlags,
  CustomAttribute,
  DefaultFilters,
} from '@accredible-frontend-v2/models';
import { AccredibleRecipientFeatureFlagsService } from '@accredible-frontend-v2/services/recipient-feature-flags';
import {
  AccredibleResponsiveBreakpoint,
  AccredibleResponsiveHelper,
} from '@accredible-frontend-v2/utils/responsive-helper';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, first, map, withLatestFrom } from 'rxjs/operators';
import { SpotlightProfilesFilters } from '../../core/models/spotlight-profiles.model';
import {
  DirectoryState,
  DirectoryStateAction,
} from '../../core/stores/directory/directory.reducer';
import {
  selectDirectory,
  selectDirectoryState,
} from '../../core/stores/directory/directory.selectors';
import {
  addQueryParamValuesToCustomAttributes,
  buildSpotlightFilters,
} from './profile-list.helper';
import { FilterService } from './services/filter.service';
import { SpotlightProfilesActions } from './store/spotlight-profiles/spotlight-profiles.actions';
import {
  SpotlightProfilesState,
  SpotlightProfilesStateAction,
} from './store/spotlight-profiles/spotlight-profiles.reducer';
import {
  selectSpotlightProfilesFilters,
  selectSpotlightProfilesPageData,
  selectSpotlightProfilesStateAction,
} from './store/spotlight-profiles/spotlight-profiles.selectors';

const PAGE_LIMIT = 500;

@Component({
  templateUrl: './profile-list.container.html',
  styleUrls: [`./profile-list.container.scss`],
})
export class ProfileListContainer implements OnInit, OnDestroy {
  private readonly _document = inject(DOCUMENT);
  private readonly _route = inject(ActivatedRoute);
  private readonly _directoryStore = inject(Store<DirectoryState>);
  private readonly _spotlightProfileStore = inject(Store<SpotlightProfilesState>);
  private readonly _filter = inject(FilterService);
  private readonly _recipientFeatureFlags = inject(AccredibleRecipientFeatureFlagsService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly directory$ = this._directoryStore.select(selectDirectory);
  readonly spotlightProfilesPageData$ = this._spotlightProfileStore.select(
    selectSpotlightProfilesPageData,
  );

  directoryFilters = signal<DefaultFilters>(null);

  directoryName: string;
  isFilterMenuOpen = false;
  isLoading = false;
  isInitialLoad = true;
  searchableCustomAttributeIds: number[];
  searchableCustomAttributes: CustomAttribute[];
  customAttributeFilters: CustomAttribute[];
  showDefaultFilters = true; // start as true because most issuers use filters
  showAllFilterOptions = true; // start as true because most issuers use filters
  pageLimitReached = false;

  @HostListener('window:resize')
  onResize(): void {
    this._checkForMobileDisplay();
  }

  ngOnInit(): void {
    // Once directory is loaded, read query params from URL and update filters on store
    this.directory$.pipe(first((directory) => !!directory?.id)).subscribe((directory) => {
      this.directoryName = directory.name;
      this._setupFilters(directory.filters);
      this._setupSearchableCustomAttributeIds(directory.custom_attributes);
      this._setupQueryParamsListener(directory.id);
      this._setupProfileLoadedTracking();
      this._setupDirectoryStateTracking();
      this.customAttributeFilters = addQueryParamValuesToCustomAttributes(
        this._route.snapshot.queryParams,
        this.searchableCustomAttributes,
      );
      this._displayFilters();
    });

    this._checkForMobileDisplay();
  }

  ngOnDestroy(): void {
    this._spotlightProfileStore.dispatch(SpotlightProfilesActions.clearFilters());
  }

  onShowFilters(fromKeyPress: boolean): void {
    this.isFilterMenuOpen = true;

    if (fromKeyPress) {
      // Focus closeFilters button if user is using keyboard (accessibility)
      this._document.getElementById('closeFilters')?.focus();
    }
  }

  onHideFilters(fromKeyPress: boolean): void {
    this.isFilterMenuOpen = false;

    if (fromKeyPress) {
      // Focus openFilters button if user is using keyboard (accessibility)
      this._document.getElementById('openFilters')?.focus();
    }
  }

  onPageChange(page: number): void {
    const isOverLimit = page > PAGE_LIMIT;
    this.pageLimitReached = isOverLimit;

    if (isOverLimit) {
      return;
    }

    this._filter.updateRouterQueryParams({ page });
  }

  private _setupFilters(filters: DefaultFilters): void {
    this.directoryFilters.set(filters);
    const {
      name,
      recipient_name,
      recipient_email,
      recipient_id,
      credential_uuid,
      ...rightHandSideFilters
    } = filters;
    // If no filters are enabled remove the filters column and button from the DOM
    this.showDefaultFilters = Object.values(rightHandSideFilters).some((filter) => filter);
  }

  private _setupSearchableCustomAttributeIds(filters: CustomAttribute[]): void {
    // Remove any custom attributes that are not searchable
    this.searchableCustomAttributes = [...filters.filter((filter) => filter.searchable)];
    // Extract the ids and assign them to the searchableCustomAttributeIds
    this.searchableCustomAttributeIds = this.searchableCustomAttributes.map(
      (customAttribute) => customAttribute.id,
    );
  }

  private _displayFilters(): void {
    this.showAllFilterOptions = this.showDefaultFilters || this.customAttributeFilters.length > 0;
  }

  private _setupProfileLoadedTracking(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesStateAction)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((action) => action === SpotlightProfilesStateAction.SPOTLIGHT_PROFILES_LOADED),
      )
      .subscribe(() => {
        this.isLoading = false;
      });
  }

  private _setupQueryParamsListener(directoryId: number): void {
    this._route.queryParams
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        withLatestFrom(this._spotlightProfileStore.select(selectSpotlightProfilesFilters)),
      )
      .subscribe(([queryParams, filters]) => {
        const { location: queryLocation } = queryParams;
        const { location: filterLocation } = filters ?? {};

        if (this._shouldLoadPlaceDetails(queryLocation, filterLocation)) {
          this._dispatchLoadPlaceDetails(directoryId, queryLocation, queryParams, filters);
        } else {
          this._dispatchLoadSpotlightProfiles(directoryId, queryParams, filters);
        }

        this._updateLoadingState();
      });
  }

  /**
   * If `queryParams.location` (placeId) is different from the current value saved in the store,
   * it means the user has changed the location filter, and we need to get the place details before making the search request
   */
  private _shouldLoadPlaceDetails(queryLocation: string, filterLocation: string): boolean {
    return queryLocation && queryLocation !== filterLocation;
  }

  private _dispatchLoadPlaceDetails(
    directoryId: number,
    queryLocation: string,
    queryParams: Params,
    filters: SpotlightProfilesFilters,
  ): void {
    this._spotlightProfileStore.dispatch(
      SpotlightProfilesActions.loadPlaceDetails({
        directoryId,
        placeId: queryLocation,
        filters: buildSpotlightFilters(queryParams, filters, this.searchableCustomAttributes),
      }),
    );
  }

  private _dispatchLoadSpotlightProfiles(
    directoryId: number,
    queryParams: Params,
    filters: SpotlightProfilesFilters,
  ): void {
    this._spotlightProfileStore.dispatch(
      SpotlightProfilesActions.loadSpotlightProfiles({
        directoryId,
        filters: {
          ...filters,
          ...buildSpotlightFilters(queryParams, filters, this.searchableCustomAttributes),
        },
      }),
    );
  }

  private _updateLoadingState(): void {
    if (this.isInitialLoad) {
      this.isInitialLoad = false;
    } else {
      this.isLoading = true;
    }
  }

  private _setupDirectoryStateTracking(): void {
    this._directoryStore
      .select(selectDirectoryState)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((state) => state.action === DirectoryStateAction.ELIGIBLE_GROUPS_LOADED),
      )
      .subscribe((state) => {
        this._setupFilters(state.directory.filters);
      });
  }

  private _checkForMobileDisplay(): void {
    if (!AccredibleResponsiveHelper.isScreenLessThan(AccredibleResponsiveBreakpoint.MD)) {
      if (this.isFilterMenuOpen) {
        this.isFilterMenuOpen = false;
      }
    }
  }
}
